import React, { useState, useEffect } from "react";
import { Logo } from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";
import { Menu, X, Globe } from "lucide-react";

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#solutions", label: t.nav.solutions, id: "nav-solutions" },
    { href: "#activate", label: t.nav.activate, id: "nav-activate" },
    { href: "#events", label: t.nav.events, id: "nav-events" },
    { href: "#access", label: t.nav.access, id: "nav-access" },
    { href: "#roadmap", label: t.nav.roadmap, id: "nav-roadmap" },
    { href: "#join", label: t.nav.join, id: "nav-join" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Logo size="md" />

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={l.id}
              className="text-sm text-zinc-400 hover:text-white transition-colors duration-300 font-body relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-gradient-to-r from-bicenter-cyan to-bicenter-violet group-hover:w-full transition-all duration-300"></span>
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div className="hidden md:flex items-center gap-1 bg-white/5 border border-white/10 rounded-full p-1" data-testid="lang-switcher">
            <button
              onClick={() => setLang("fr")}
              data-testid="lang-fr"
              className={`px-3 py-1 text-xs rounded-full transition-all ${
                lang === "fr" ? "bg-white text-black font-semibold" : "text-zinc-400 hover:text-white"
              }`}
            >
              FR
            </button>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-3 py-1 text-xs rounded-full transition-all ${
                lang === "en" ? "bg-white text-black font-semibold" : "text-zinc-400 hover:text-white"
              }`}
            >
              EN
            </button>
          </div>

          <a
            href="#demo"
            data-testid="header-cta-demo"
            className="hidden md:inline-flex items-center gap-2 bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white px-5 py-2.5 rounded-full text-sm font-medium hover:shadow-[0_0_30px_rgba(0,169,255,0.45)] transition-all duration-300"
          >
            {t.nav.demo}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-zinc-300 hover:text-white py-2 text-sm font-body"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-2 mt-2">
              <Globe size={14} className="text-zinc-500" />
              <button
                onClick={() => setLang("fr")}
                className={`px-3 py-1 text-xs rounded-full ${lang === "fr" ? "bg-white text-black" : "text-zinc-400 border border-white/10"}`}
              >
                FR
              </button>
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1 text-xs rounded-full ${lang === "en" ? "bg-white text-black" : "text-zinc-400 border border-white/10"}`}
              >
                EN
              </button>
            </div>
            <a
              href="#demo"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white px-5 py-3 rounded-full text-sm font-medium mt-2"
            >
              {t.nav.demo}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
